<template>
    <div class="job-detail-root">
        <Header :label="data().title" overlap="large" />
        <TopDetails :location="getLocation()" :time="getDeadline()" />
        <MainLayout margin-top left-to-bottom-right hideOnMobile="left">
            <ProfileModule slot="left" />
            <FeaturedResource slot="left" />

            <DescriptionModule
                slot="center"
                :data="data()"
                :isLoading="loading()"
            />
            <CompanyModule
                slot="right"
                :id="data().companyId"
                :showFavorite="true"
                :jobAdId="jobAdId"
                :isLoading="loading()"
            >
            </CompanyModule>
            <DetailModule
                slot="right"
                :data="data()"
                :postingId="postingId"
                :location="getLocation()"
                :deadline="getDeadline()"
                :RequestId="requestId"
                :impressionEventId="impressionEventId"
                :isVerified="Boolean(currentUniversityVerified)"
                :isPending="Boolean(currentUniversityPending)"
                :isLoading="loading()"
            />
            <!-- <TagModule slot="right" :data="data()" /> -->
        </MainLayout>
    </div>
</template>

<script>
// Project Components
import Header from '~/components/layouts/Header';
import MainLayout from '~/components/layouts/mainlayout/MainLayout';
import CompanyModule from '~/components/smart/CompanyModule';
import ProfileModule from '~/components/smart/ProfileModule/ProfileModule';
import FeaturedResource from '~/components/smart/FeaturedResource';

// Page Components
import TopDetails from './components/TopDetails';
import DescriptionModule from './components/DescriptionModule';
import DetailModule from './components/DetailModule';
// import TagModule from './components/TagModule';

// Utils
import UniversityService from '~/services/UniversityService';
import AnalyticService from '~/services/AnalyticService';
import dateToString from '~/utils/dateToString';
import uuidv4 from '~/utils/uuidv4';
import { getSubDomain } from '~/utils/urlUtils';

export default {
    name: 'JobDetail',
    components: {
        Header,
        MainLayout,
        TopDetails,
        DescriptionModule,
        CompanyModule,
        DetailModule,
        ProfileModule,
        FeaturedResource,
        //  TagModule,
    },

    data: () => ({
        postingId: null,
        requestId: null,
        impressionEventId: null,
        title: '',
        location: '',
        deadline: '',

        // For analytics logging
        hasLoggedEvent: false,
    }),
    computed: {
        posting() {
            if (this.$route.params.id) {
                return this.$interfaces.posting.id(this.$route.params.id).get({
                    reactive: true,
                    isJobAdId: Boolean(this.$route.query.jobAdId),
                });
            } else {
                return null;
            }
        },
        jobAdId() {
            return this.data() ? this.data().jobAdId : null;
        },
        isLoading() {
            return this.loading();
        },
        currentUniversityVerified() {
            return this.verifiedUnis.find(
                it =>
                    (it.school.subDomain || '').toLowerCase() ===
                    this.currentUni
            );
        },
        currentUniversityPending() {
            return this.pendingUnis.find(
                it =>
                    (it.school.subDomain || '').toLowerCase() ===
                    this.currentUni
            );
        },
        currentUni() {
            return UniversityService.getCurrentDomain();
        },
        uniConnections() {
            return this.$interfaces.university.connection.get({
                reactive: true,
            });
        },
        verifiedUnis() {
            if (!this.uniConnections.data) return [];
            return this.uniConnections.data.filter(c => c.isVerified);
        },
        pendingUnis() {
            if (!this.uniConnections.data) return [];
            return this.uniConnections.data.filter(c => c.isVerified === false);
        },
    },
    methods: {
        data() {
            return !(this.posting.data instanceof Array)
                ? this.posting.data
                : {};
        },
        loading() {
            return this.posting.loading;
        },
        error() {
            return this.posting.error;
        },
        getLocation() {
            const data = this.data();
            return ''.concat(
                data.location ? data.location : '',
                data.country ? ', '.concat(data.country) : ''
            );
        },
        getDeadline() {
            return dateToString(this.data().deadline);
        },
        getCompanyInfo() {
            const data = this.data();
            return {
                companyId: data.companyId,
                companyLogo: data.companyLogo,
                companyName: data.companyName,
                description: data.companyDescription,
                location: data.location,
                country: data.country,
            };
        },
        logPageviewEvent() {
            // This triggers an Google Analytics event
            const data = this.data();
            if (data.jobAdId) {
                AnalyticService.jobadViewEvent(
                    data.jobAdId,
                    data.companyId,
                    data.title
                );
                this.hasLoggedEvent = true;
            }
        },
    },
    watch: {
        isLoading(isLoading, wasLoading) {
            if (!isLoading && wasLoading) {
                if (this.requestId && this.eventId) {
                    const newEventId = 'JobsDetailView' + uuidv4();
                    this.impressionEventId = newEventId;

                    this.$interfaces.event.post({
                        body: {
                            requestId: this.requestId,
                            eventId: newEventId,
                            parentEventId: this.eventId,
                            createTime: new Date().toISOString(),
                            jobEvent: {
                                type: 'VIEW',
                                jobs: [this.data().nameId],
                            },
                        },
                    });
                }
            }
        },
        posting(newPosting, oldPosting) {
            // When the posting data has changed, the posting-data has loaded.
            // Log the posting-pageview if it has not been logged before, or
            // if the posting has changed (the ids have changed).
            if (!this.hasLoggedEvent || newPosting.ids !== oldPosting.ids) {
                this.logPageviewEvent();
            }
        },
    },
    mounted() {
        window.scrollTo(0, 0);

        // Try to log job-post pageview. Will only log if data is already stored in the store
        this.logPageviewEvent();
    },
    created() {
        this.postingId = this.$route.params.id;
        this.requestId = this.$route.query.RequestId
            ? this.$route.query.RequestId
            : null;
        this.eventId = this.$route.query.EventId
            ? this.$route.query.EventId
            : null;

        // console.log(this.$route);
        this.$interfaces.university.connection.get();
    },
};
</script>

<style scoped>
.job-detail-root {
    padding-bottom: 100px;
}
</style>
